import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import classNames from 'classnames'
import { GripVertical, X } from 'lucide-react'
import { useEffect, useRef, type KeyboardEvent } from 'react'
import type { OptionRowProps } from './option-row.interface'
import classes from './option-row.module.scss'

export function OptionRow({
  option,
  index,
  error,
  focusRequested,
  onFocused,
  onChange,
  onEnter,
  onRemove,
}: OptionRowProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: option.key,
  })
  const inputRef = useRef<HTMLInputElement>(null)
  const errorId = `${option.key}-error`
  const name = option.value || `Option ${index + 1}`

  useEffect(() => {
    const input = inputRef.current
    if (!focusRequested || !input) return
    input.focus()
    input.setSelectionRange(input.value.length, input.value.length)
    onFocused()
  }, [focusRequested, onFocused])

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
    event.preventDefault()
    onEnter()
  }

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={classNames(classes.root, { [classes.dragging]: isDragging })}
      data-option-key={option.key}
    >
      <div className={classes.row}>
        <button
          type="button"
          ref={setActivatorNodeRef}
          className={classes.handle}
          {...attributes}
          {...listeners}
          aria-label={`Reorder option ${name}`}
        >
          <GripVertical size={14} aria-hidden="true" />
        </button>
        <input
          ref={inputRef}
          className={classes.input}
          value={option.value}
          aria-label={`Option ${index + 1}`}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button type="button" className={classes.remove} aria-label={`Remove option ${name}`} onClick={onRemove}>
          <X size={16} strokeWidth={2.2} aria-hidden="true" />
        </button>
      </div>
      {error && (
        <div id={errorId} className={classes.error}>
          {error}
        </div>
      )}
    </li>
  )
}
